import { formatFileSize } from '../crypto/sharedFolderFile';
import {
  listDeletedSharedFolderFiles,
  revertDeletedSharedFolderFile,
  type DeletedFileItem,
} from '../crypto/sharedFolderDeletion';
import type { SharedFolderManifestPlaintext } from '../crypto/sharedFolderManifest';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { showConfirmDialog } from './confirmDialog';
import { enhanceDataTableCards } from './dataTableCards';

export function splitDeletedFilePath(path: string): { name: string; location: string } {
  const slash = path.lastIndexOf('/');
  if (slash < 0) {
    return { name: path, location: '/' };
  }
  return {
    name: path.slice(slash + 1),
    location: path.slice(0, slash) || '/',
  };
}

export interface SharedFolderDeletionHistoryPanelOptions {
  folderId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  onReverted: (result: { revision: number; manifest: SharedFolderManifestPlaintext }) => void;
}

export async function renderSharedFolderDeletionHistoryPanel(
  host: HTMLElement,
  input: SharedFolderDeletionHistoryPanelOptions,
): Promise<() => void> {
  host.innerHTML = `<p class="muted" data-i18n="status.loading"></p>`;
  applyI18n(host);

  let deletedItems: DeletedFileItem[] = [];
  let busy = false;
  const state = {
    revision: input.revision,
    manifest: input.manifest,
  };

  try {
    deletedItems = await listDeletedSharedFolderFiles(
      input.folderId,
      input.folderKey,
      input.manifest,
    );
  } catch (cause) {
    console.error('Load deletion history failed', cause);
    host.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
    applyI18n(host);
    return () => undefined;
  }

  const panel = document.createElement('div');
  panel.className = 'files-deletion-history-panel';
  panel.innerHTML = `
    <p class="muted" data-i18n="files.owner.deletionHistorySubtitle"></p>
    <div class="files-table-wrap">
      <table class="data-table files-access-table">
        <thead>
          <tr>
            <th data-i18n="files.owner.fileName"></th>
            <th data-i18n="files.owner.propertiesLocation"></th>
            <th data-i18n="files.owner.fileSize"></th>
            <th data-i18n="files.owner.deletedAt"></th>
            <th class="data-table__actions-heading" data-i18n="files.owner.listActions"></th>
          </tr>
        </thead>
        <tbody id="files-deletion-history-body"></tbody>
      </table>
    </div>
    <p class="muted" id="files-deletion-history-empty" hidden data-i18n="files.owner.deletionHistoryEmpty"></p>
    <p class="error-text" id="files-deletion-history-error" hidden></p>
  `;

  host.replaceChildren(panel);
  applyI18n(panel);

  const tbody = panel.querySelector('#files-deletion-history-body') as HTMLTableSectionElement;
  const emptyEl = panel.querySelector('#files-deletion-history-empty') as HTMLParagraphElement;
  const errorEl = panel.querySelector('#files-deletion-history-error') as HTMLParagraphElement;

  const showError = (message: string) => {
    errorEl.hidden = false;
    errorEl.textContent = message;
  };

  const renderRows = () => {
    tbody.replaceChildren();
    emptyEl.hidden = deletedItems.length > 0;

    for (const item of deletedItems) {
      const row = document.createElement('tr');
      const { name, location } = splitDeletedFilePath(item.entry.path);

      const nameCell = document.createElement('td');
      nameCell.textContent = name;

      const locationCell = document.createElement('td');
      locationCell.className = 'files-deletion-history__location';
      locationCell.textContent = location;
      locationCell.title = item.entry.path;

      const sizeCell = document.createElement('td');
      sizeCell.textContent = formatFileSize(item.entry.size);

      const deletedCell = document.createElement('td');
      deletedCell.textContent = new Date(item.deletedAt).toLocaleString();

      const actionsCell = document.createElement('td');
      actionsCell.className = 'data-table__actions';
      const revertButton = document.createElement('button');
      revertButton.type = 'button';
      revertButton.className = 'button ghost';
      revertButton.innerHTML = `<span data-i18n="files.owner.revert"></span>`;
      revertButton.addEventListener('click', () => {
        void (async () => {
          if (busy) {
            return;
          }

          const confirmed = await showConfirmDialog({
            titleKey: 'files.owner.revertFileTitle',
            messageKey: 'files.owner.revertFileConfirm',
            confirmLabelKey: 'files.owner.revert',
            confirmClassName: 'primary',
          });
          if (!confirmed) {
            return;
          }

          busy = true;
          errorEl.hidden = true;
          revertButton.disabled = true;
          try {
            const result = await revertDeletedSharedFolderFile({
              folderId: input.folderId,
              folderKey: input.folderKey,
              revision: state.revision,
              manifest: state.manifest,
              deletedItem: item,
            });
            state.revision = result.revision;
            state.manifest = result.manifest;
            input.onReverted({ revision: result.revision, manifest: result.manifest });
            deletedItems = deletedItems.filter((entry) => entry.entry.fileId !== item.entry.fileId);
            renderRows();
          } catch {
            showError(t('status.error'));
          } finally {
            busy = false;
            revertButton.disabled = false;
          }
        })();
      });
      actionsCell.append(revertButton);

      row.append(nameCell, locationCell, sizeCell, deletedCell, actionsCell);
      tbody.append(row);
    }

    applyI18n(tbody);
    const deletionTable = panel.querySelector('table.data-table') as HTMLTableElement | null;
    if (deletionTable) {
      enhanceDataTableCards(deletionTable);
    }
  };

  renderRows();

  const unbindLocale = bindLocaleRefresh(() => {
    renderRows();
    applyI18n(panel);
  });

  return () => {
    unbindLocale();
  };
}
